/*!
 * Kwolly — Cart Undo Toast v1.0
 *
 * Shows a 3.5-second dismissible toast after an item is removed from cart.
 * The "Ongedaan maken" button re-adds the item via /cart/add.js.
 *
 * Requirements:
 *   - Remove links must carry data-variant-id, data-quantity, data-product-title
 *     (added to snippets/line-item.liquid)
 *
 * Strategy:
 *   - Toast is appended to <body> so it survives cart section re-renders
 *   - Uses event delegation on document so it works after AJAX re-renders
 *   - After undo, dispatches cart:refresh / cart:updated for Impact theme pickup
 *   - Falls back to cartDrawer.refresh() if the custom element exposes it
 */

(function kwollyCartUndo() {
  'use strict';

  // ── Create toast element as a body portal ─────────────────────
  // Appended to body so it's NOT inside <cart-drawer> and survives
  // section re-renders triggered by cart AJAX updates.
  const toast = document.createElement('div');
  toast.className = 'kwolly-undo-toast';
  toast.setAttribute('aria-live', 'assertive');
  toast.setAttribute('role', 'status');
  toast.hidden = true;
  toast.innerHTML =
    '<span class="kwolly-undo-toast__msg"></span>' +
    '<button type="button" class="kwolly-undo-toast__btn">Ongedaan maken</button>';
  document.body.appendChild(toast);

  let pendingUndo = null;
  let dismissTimer = null;

  // ── Show toast ────────────────────────────────────────────────
  function showToast(title, variantId, qty) {
    pendingUndo = {
      variantId: parseInt(variantId, 10),
      qty: Math.max(1, parseInt(qty, 10) || 1),
    };

    toast.querySelector('.kwolly-undo-toast__msg').textContent =
      '\u201c' + title + '\u201d verwijderd';

    toast.hidden = false;
    void toast.offsetHeight; // force reflow so CSS transition fires
    toast.classList.add('is-visible');

    clearTimeout(dismissTimer);
    dismissTimer = setTimeout(dismissToast, 3500);
  }

  // ── Dismiss toast ─────────────────────────────────────────────
  function dismissToast() {
    toast.classList.remove('is-visible');
    // Wait for CSS transition before hiding from accessibility tree
    setTimeout(() => {
      toast.hidden = true;
      pendingUndo = null;
    }, 320);
  }

  // ── Undo handler ──────────────────────────────────────────────
  toast.querySelector('.kwolly-undo-toast__btn').addEventListener('click', async () => {
    if (!pendingUndo) return;
    const { variantId, qty } = pendingUndo;
    clearTimeout(dismissTimer);
    dismissToast();

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: qty }),
      });

      if (!res.ok) {
        console.warn('[kwolly-undo] /cart/add.js returned', res.status);
        return;
      }

      // Dispatch events Impact/Shopify themes typically listen to for cart refresh.
      // Multiple names used for compatibility across theme versions.
      ['cart:refresh', 'cart:updated', 'cart:change'].forEach((name) => {
        document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: {} }));
      });

      // If the cart-drawer custom element exposes a refresh method, call it directly.
      setTimeout(() => {
        const drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.refresh === 'function') {
          drawer.refresh();
        }
      }, 80);

    } catch (err) {
      console.warn('[kwolly-undo] Re-add failed:', err);
    }
  });

  // ── Intercept remove link clicks (event delegation) ───────────
  // Using document-level listener so it still works after AJAX re-renders
  // re-inject new .kwolly-remove-link elements into the DOM.
  document.addEventListener('click', (e) => {
    const link = e.target.closest('.kwolly-remove-link[data-variant-id]');
    if (!link) return;

    const { variantId, quantity, productTitle } = link.dataset;
    if (!variantId) return;

    showToast(productTitle || 'Product', variantId, quantity || '1');
    // Do NOT preventDefault — let the Impact theme's own removal AJAX proceed normally.
  });
})();
